import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const SettingsContext = createContext(undefined);

// Backend API URL - reads/writes public/config/settings.json (later can switch to DB)
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const SETTINGS_JSON_PATH = '/config/settings.json';
const BRAND_THEME_COLORS = {
  primary: '#3B5BDB',
  secondary: '#28ACA8',
  accent: '#28ACA8',
  success: '#15803D',
  warning: '#B45309',
  error: '#DC2626',
  info: '#3B5BDB'
};

const LEGACY_THEME_VARIANTS = [
  { primary: '#1D4ED8', secondary: '#0F766E', accent: '#C2410C', info: '#2563EB' },
  { primary: '#2563EB', secondary: '#0F766E', accent: '#C2410C', info: '#2563EB' },
  { primary: '#3B5BDB', secondary: '#20C997' },
];

const defaultSettings = {
  app: {
    name: 'Wizbox',
    tagline: 'AI-Powered Talent Management',
    logo: '/config/logo.svg',
    favicon: '/config/favicon.ico'
  },
  theme: {
    mode: 'light',
    colors: BRAND_THEME_COLORS
  },
  features: {
    darkMode: true,
    showTagline: true,
    allowRegistration: true,
    socialLogin: {
      google: true,
      linkedin: true
    }
  }
};

const STORAGE_KEY = 'talentgpt_settings';

function normalizeThemeColors(colors = {}) {
  const primary = (colors.primary || '').toUpperCase();
  const secondary = (colors.secondary || '').toUpperCase();
  const accent = (colors.accent || '').toUpperCase();
  const info = (colors.info || '').toUpperCase();

  const shouldMigrate = LEGACY_THEME_VARIANTS.some((variant) => {
    const variantPrimary = (variant.primary || '').toUpperCase();
    const variantSecondary = (variant.secondary || '').toUpperCase();
    const variantAccent = (variant.accent || '').toUpperCase();
    const variantInfo = (variant.info || '').toUpperCase();

    if (primary !== variantPrimary || secondary !== variantSecondary) return false;
    if (variantAccent && accent && accent !== variantAccent) return false;
    if (variantInfo && info && info !== variantInfo) return false;
    return true;
  });

  if (!shouldMigrate) return colors;

  return {
    ...colors,
    ...BRAND_THEME_COLORS,
  };
}

/** Deep merge saved object into defaults so we never miss keys */
function mergeWithDefaults(saved) {
  if (!saved || typeof saved !== 'object') return defaultSettings;
  const mergedApp = { ...defaultSettings.app, ...(saved.app || {}) };
  if ((mergedApp.name || '').trim().toLowerCase() === 'talentgpt') {
    mergedApp.name = 'Wizbox';
  }
  const mergedColors = normalizeThemeColors({
    ...defaultSettings.theme.colors,
    ...(saved.theme?.colors || {}),
  });
  return {
    app: mergedApp,
    theme: {
      ...defaultSettings.theme,
      ...(saved.theme || {}),
      colors: mergedColors,
    },
    features: {
      ...defaultSettings.features,
      ...(saved.features || {}),
      socialLogin: { ...defaultSettings.features.socialLogin, ...(saved.features?.socialLogin || {}) },
    },
  };
}

function getStoredSettings() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return mergeWithDefaults(parsed);
  } catch {
    return null;
  }
}

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(() => getStoredSettings() || defaultSettings);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadSettings = useCallback(async () => {
    try {
      setIsLoading(true);

      // 1) Try backend API (reads public/config/settings.json; later can be DB)
      try {
        const response = await fetch(`${API_BASE_URL}/settings`);
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            const merged = mergeWithDefaults(data.data);
            setSettings(merged);
            setError(null);
            localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
            return;
          }
        }
      } catch (apiErr) {
        console.warn('API unavailable, reading from JSON file or cache:', apiErr);
      }

      // 2) Read from JSON file in public folder (same file the API would read)
      try {
        const fileResponse = await fetch(SETTINGS_JSON_PATH);
        if (fileResponse.ok) {
          const data = await fileResponse.json();
          const merged = mergeWithDefaults(data);
          setSettings(merged);
          setError(null);
          localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
          return;
        }
      } catch (fileErr) {
        console.warn('Could not load settings.json:', fileErr);
      }

      // 3) Fallback to localStorage (e.g. when no server and file not yet written)
      const stored = getStoredSettings();
      if (stored) {
        setSettings(stored);
        setError(null);
        return;
      }

      setError(null);
    } catch (err) {
      console.warn('Could not load settings, using defaults:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    const root = document.documentElement;
    const colors = settings.theme?.colors;
    
    if (colors) {
      Object.entries(colors).forEach(([key, value]) => {
        root.style.setProperty(`--color-${key}`, value);
      });
      root.style.setProperty('--brand-gradient', `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`);
      root.style.setProperty('--brand-gradient-soft', `linear-gradient(135deg, ${colors.primary}22 0%, ${colors.secondary}22 100%)`);
    }
  }, [settings.theme]);

  const updateSettings = useCallback((path, value) => {
    setSettings(prev => {
      const newSettings = { ...prev };
      const keys = path.split('.');
      let current = newSettings;
      
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return newSettings;
    });
  }, []);

  const saveSettings = useCallback(async () => {
    try {
      // Always persist to localStorage so settings survive refresh
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));

      // Write to JSON file via API (backend writes to public/config/settings.json; later can be DB)
      try {
        const response = await fetch(`${API_BASE_URL}/settings`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(settings),
        });
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(mergeWithDefaults(data.data)));
            return { success: true };
          }
        }
      } catch (apiErr) {
        console.warn('API unavailable, settings saved to browser only:', apiErr);
      }

      return { success: true };
    } catch (err) {
      console.error('Failed to save settings:', err);
      return { success: false, error: err.message };
    }
  }, [settings]);

  const resetSettings = useCallback(async () => {
    try {
      // Try to reset via API
      try {
        const response = await fetch(`${API_BASE_URL}/settings/reset`, {
          method: 'POST',
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            setSettings(mergeWithDefaults(data.data));
            localStorage.removeItem(STORAGE_KEY);
            return;
          }
        }
      } catch (apiErr) {
        console.warn('Could not reset via API:', apiErr);
      }
      
      // Fallback to local reset
      setSettings(defaultSettings);
      localStorage.removeItem(STORAGE_KEY);
    } catch (err) {
      console.error('Failed to reset settings:', err);
    }
  }, []);

  return (
    <SettingsContext.Provider 
      value={{ 
        settings, 
        isLoading, 
        error, 
        updateSettings, 
        saveSettings, 
        resetSettings,
        reloadSettings: loadSettings 
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

export default SettingsContext;
